/**
 * Workplace compliance: Employment Hero certifications (police checks,
 * NDIS worker screening, WWCC, licences, AHPRA) plus eTrainu courses, rolled
 * up into the three backend categories. ET items read the same records as
 * the Training page (training.ts) so the two pages always agree.
 */
import { Persona } from '../../personas';
import { rng } from '../../rng';
import { SITES, STAFF, Staff } from '../../world';
import { activeToday, daily, daysBetween, ehId, isCare, isFrontline, isRemote, pctInt, shiftDay, shiftMonths, today } from './common';
import { gapFor, ndisGaps } from './gaps';
import { trainingIndex } from './training';

export type ItemStatus = 'complete' | 'expired' | 'missing';

interface StaffItemState {
    status: ItemStatus;
    certName: string | null;
    expiry: string | null;
}

interface ItemDef {
    name: string;
    source: 'EH' | 'ET';
    ehCertNames: string[];
    etCourseNames: string[];
    applies: (s: Staff) => boolean;
    state: (s: Staff) => StaffItemState;
}

export interface CategoryDef {
    key: 'workforce_compliance' | 'mandatory_training' | 'essential_training';
    name: string;
    scope: (s: Staff) => boolean;
    items: ItemDef[];
}

const REGION_OF_SITE = (s: Staff): 'NT' | 'SA' | 'WA' => SITES.find(x => x.name === s.site)?.region ?? 'NT';

const WWCC_CERT = { NT: 'Ochre Card (NT WWCC)', SA: 'SA Working with Children Check', WA: 'WA Working with Children Card' };
const LICENCE_CERT = { NT: 'NT Driver Licence', SA: 'SA Driver Licence', WA: 'WA Driver Licence' };

/** A generic EH certification: current unless the gap plan says it has lapsed or was never uploaded. */
function ehCert(name: string, certName: (s: Staff) => string, validityMonths: number | null) {
    return (s: Staff): StaffItemState => {
        const t = today();
        const r = rng(`people:eh:${s.id}:${name}`);
        const gap = gapFor(s.id, name);
        if (gap === 'missing' || gap === 'in_progress' || (gap === 'expired' && !validityMonths)) {
            return { status: 'missing', certName: null, expiry: null };
        }
        if (!validityMonths) {
            return { status: 'complete', certName: certName(s), expiry: null };
        }
        if (gap === 'expired') {
            return { status: 'expired', certName: certName(s), expiry: shiftDay(t, -r.int(3, 120)) };
        }
        const span = daysBetween(t, shiftMonths(t, validityMonths));
        return { status: 'complete', certName: certName(s), expiry: shiftDay(t, r.int(5, span - 1)) };
    };
}

/** NDIS Worker Screening: the explicit exceptions from gaps.ts, everyone else current. */
function ndisState(s: Staff): StaffItemState {
    const gaps = ndisGaps();
    if (gaps.missing.has(s.id)) {
        return { status: 'missing', certName: null, expiry: null };
    }
    if (gaps.expired.has(s.id)) {
        return { status: 'expired', certName: 'NDIS Worker Screening Check', expiry: gaps.expired.get(s.id)! };
    }
    const r = rng(`people:eh:${s.id}:ndis`);
    return { status: 'complete', certName: 'NDIS Worker Screening Check', expiry: shiftDay(today(), r.int(60, 1800)) };
}

/** AHPRA registrations all renew on 31 May. */
function ahpraState(): StaffItemState {
    const t = today();
    const y = Number(t.slice(0, 4));
    const may31 = `${y}-05-31` >= t ? `${y}-05-31` : `${y + 1}-05-31`;
    return { status: 'complete', certName: 'AHPRA Registration (RN)', expiry: may31 };
}

/** ET item state from the shared eTrainu records. */
function etState(course: string) {
    return (s: Staff): StaffItemState => {
        const r = trainingIndex().get(`${s.id}|${course}`);
        if (!r || r.status !== 'complete') {
            return { status: 'missing', certName: null, expiry: null };
        }
        if (r.expiryDate && r.expiryDate < today()) {
            return { status: 'expired', certName: course, expiry: r.expiryDate };
        }
        return { status: 'complete', certName: course, expiry: r.expiryDate };
    };
}

const all = () => true;

function etItem(course: string, applies: (s: Staff) => boolean): ItemDef {
    return { name: course, source: 'ET', ehCertNames: [], etCourseNames: [course], applies, state: etState(course) };
}

export const CATEGORY_DEFS: CategoryDef[] = [
    {
        key: 'workforce_compliance',
        name: 'Workforce Compliance',
        scope: all,
        items: [
            {
                name: 'Police Clearance',
                source: 'EH',
                ehCertNames: ['National Police Check'],
                etCourseNames: [],
                applies: all,
                state: ehCert('Police Clearance', () => 'National Police Check', 36),
            },
            {
                name: 'NDIS Worker Screening Clearance',
                source: 'EH',
                ehCertNames: ['NDIS Worker Screening Check'],
                etCourseNames: [],
                applies: all,
                state: ndisState,
            },
            {
                name: 'Working with Children Check',
                source: 'EH',
                ehCertNames: Object.values(WWCC_CERT),
                etCourseNames: [],
                applies: all,
                state: ehCert('Working with Children Check', s => WWCC_CERT[REGION_OF_SITE(s)], 36),
            },
            {
                name: 'Drivers Licence',
                source: 'EH',
                ehCertNames: Object.values(LICENCE_CERT),
                etCourseNames: [],
                applies: all,
                state: ehCert('Drivers Licence', s => LICENCE_CERT[REGION_OF_SITE(s)], 60),
            },
            {
                name: 'RN Registration (AHPRA)',
                source: 'EH',
                ehCertNames: ['AHPRA Registration (RN)'],
                etCourseNames: [],
                applies: s => s.position === 'Registered Nurse',
                state: ahpraState,
            },
        ],
    },
    {
        key: 'mandatory_training',
        name: 'Mandatory Training Compliance',
        scope: all,
        items: [
            {
                name: 'Code of Conduct & Induction',
                source: 'EH',
                ehCertNames: ['Signed Code of Conduct'],
                etCourseNames: [],
                applies: all,
                state: ehCert('Code of Conduct & Induction', () => 'Signed Code of Conduct', null),
            },
            etItem('NDIS Worker Orientation Module', all),
            etItem('Cultural Safety', all),
            etItem('Infection Control', all),
            etItem('Manual Handling', all),
            etItem('DFV Awareness', all),
        ],
    },
    {
        key: 'essential_training',
        name: 'Essential Training',
        scope: isFrontline,
        items: [
            etItem('First Aid', isFrontline),
            etItem('CPR', isFrontline),
            etItem('Trauma-Informed Practice', isFrontline),
            etItem('Medication Administration', isCare),
            etItem('Remote Driving 4WD', s => isFrontline(s) && isRemote(s)),
        ],
    },
];

interface ItemRow {
    staff: Staff;
    state: StaffItemState;
}

/** Every (in-scope active staff × item) state, per category and item. */
function complianceMatrix(): { def: CategoryDef; employees: Staff[]; items: { def: ItemDef; rows: ItemRow[] }[] }[] {
    return daily('people:compliance:matrix', () => {
        const active = [...activeToday()].sort((a, b) => a.name.localeCompare(b.name));
        return CATEGORY_DEFS.map(def => {
            const employees = active.filter(def.scope);
            return {
                def,
                employees,
                items: def.items.map(item => ({ def: item, rows: employees.filter(item.applies).map(s => ({ staff: s, state: item.state(s) })) })),
            };
        });
    });
}

function buildCategories() {
    return complianceMatrix().map(({ def, employees, items }) => {
        const outItems = items.map(({ def: item, rows }) => {
            const complete = rows.filter(r => r.state.status === 'complete').length;
            const expired = rows.filter(r => r.state.status === 'expired');
            const missing = rows.filter(r => r.state.status === 'missing');
            return {
                name: item.name,
                source: item.source,
                eh_cert_names: item.ehCertNames,
                et_course_names: item.etCourseNames,
                complete,
                expired: expired.length,
                missing: missing.length,
                total: rows.length,
                rate: pctInt(complete, rows.length),
                expired_employees: expired.map(r => ({ employee_id: ehId(r.staff), name: r.staff.name, cert_name: r.state.certName, expiry_date: r.state.expiry })),
                missing_employees: missing.map(r => ({ employee_id: ehId(r.staff), name: r.staff.name })),
            };
        });
        const complete = outItems.reduce((n, i) => n + i.complete, 0);
        const totalChecks = outItems.reduce((n, i) => n + i.total, 0);
        return {
            key: def.key,
            name: def.name,
            rate: pctInt(complete, totalChecks),
            complete,
            total_checks: totalChecks,
            total_employees: employees.length,
            items: outItems,
        };
    });
}

export type ComplianceCategory = ReturnType<typeof buildCategories>[number];

/** `compliance_by_category` for /api/eh-data/stats. Board drops the per-person lists. */
export function complianceByCategory(board = false): { categories: any[] } {
    const cats = daily('people:compliance:categories', buildCategories);
    if (!board) {
        return { categories: cats };
    }
    return {
        categories: cats.map(c => ({
            key: c.key,
            name: c.name,
            rate: c.rate,
            items: c.items.map(i => ({ name: i.name, source: i.source, total: i.total, complete: i.complete, expired: i.expired, missing: i.missing, rate: i.rate })),
        })),
    };
}

/** Headline compliance figures (dashboard banner, board, CEO report, assistant). */
export function complianceSummary() {
    const cats = daily('people:compliance:categories', buildCategories);
    const items = cats.flatMap(c => c.items);
    const ndis = items.find(i => i.name === 'NDIS Worker Screening Clearance')!;
    const sum = (k: 'total' | 'complete' | 'expired' | 'missing') => items.reduce((n, i) => n + i[k], 0);
    const rate = (key: string) => cats.find(c => c.key === key)?.rate ?? 0;
    const below = cats.flatMap(c => c.items.filter(i => i.rate < (c.key === 'essential_training' ? 80 : 90)).map(i => ({ category: c.name, name: i.name, rate: i.rate })));
    return {
        workforce_rate: rate('workforce_compliance'),
        mandatory_rate: rate('mandatory_training'),
        essential_rate: rate('essential_training'),
        overall_rate: pctInt(sum('complete'), sum('total')),
        total_checks: sum('total'),
        complete: sum('complete'),
        expired: sum('expired'),
        missing: sum('missing'),
        ndis_worker_screening: { total: ndis.total, complete: ndis.complete, expired: ndis.expired, missing: ndis.missing, rate: ndis.rate },
        items_below_target: below,
        categories: cats.map(c => ({ key: c.key, name: c.name, rate: c.rate, complete: c.complete, total_checks: c.total_checks, total_employees: c.total_employees })),
    };
}

/** GET /api/eh-data/compliance-export?category=<key> */
export function complianceExport(category: string | null) {
    const cat = complianceMatrix().find(c => c.def.key === category);
    if (!cat) {
        return { category, count: 0, rows: [] as unknown[], message: 'Unknown compliance category.' };
    }
    const rows = cat.items.flatMap(({ def, rows: rs }) =>
        rs.map(r => ({
            employee: r.staff.name,
            item: def.name,
            source: def.source === 'EH' ? 'Employment Hero' : 'eTrainu',
            status: r.state.status === 'complete' ? 'current' : r.state.status,
            evidence: r.state.certName,
            expiry_date: r.state.expiry,
        })),
    );
    return { category, count: rows.length, rows, message: null as string | null };
}

/** Staff named in an expired/missing list — handy for the assistant. */
export function complianceGapsFor(staffId: number): { item: string; status: ItemStatus }[] {
    const s = STAFF.find(x => x.id === staffId);
    if (!s) {
        return [];
    }
    return complianceMatrix().flatMap(c =>
        c.items.flatMap(({ def, rows }) => rows.filter(r => r.staff.id === s.id && r.state.status !== 'complete').map(r => ({ item: def.name, status: r.state.status }))),
    );
}

// ---------------------------------------------------------------------------
// /api/me/compliance — the signed-in persona's own record.
// Leadership personas aren't in the frontline roster, so their record is
// authored here: everything current except a few believable gaps.
// ---------------------------------------------------------------------------

type TrainingItemStatus = 'complete' | 'in_progress' | 'expired' | 'missing';

interface MeOverride {
    status: TrainingItemStatus;
    /** Days from today for expiry (negative = lapsed). */
    expiresIn?: number;
    progress?: string;
}

const ME_OVERRIDES: Record<string, Record<string, MeOverride>> = {
    ceo: {
        'DFV Awareness': { status: 'in_progress', progress: '60%' },
        CPR: { status: 'complete', expiresIn: 47 },
    },
    coo: {
        'Remote Driving 4WD': { status: 'missing' },
        'Infection Control': { status: 'in_progress', progress: '40%' },
        'Cultural Safety': { status: 'complete', expiresIn: 64 },
    },
    care: {
        'Medication Administration': { status: 'expired', expiresIn: -9 },
        'Trauma-Informed Practice': { status: 'in_progress', progress: '80%' },
        CPR: { status: 'complete', expiresIn: 33 },
    },
    hr: {
        'DFV Awareness': { status: 'missing' },
        'First Aid': { status: 'expired', expiresIn: -15 },
        'Manual Handling': { status: 'complete', expiresIn: 71 },
    },
};

const ME_ESSENTIAL: Record<string, string[]> = {
    ceo: ['First Aid', 'CPR'],
    coo: ['First Aid', 'CPR', 'Trauma-Informed Practice', 'Remote Driving 4WD'],
    care: ['First Aid', 'CPR', 'Trauma-Informed Practice', 'Medication Administration', 'Remote Driving 4WD'],
    hr: ['First Aid', 'CPR'],
};

const VALIDITY: Record<string, number | null> = {
    'Code of Conduct & Induction': null,
    'NDIS Worker Orientation Module': null,
    'Cultural Safety': 36,
    'Infection Control': 24,
    'Manual Handling': 24,
    'DFV Awareness': null,
    'First Aid': 36,
    CPR: 12,
    'Trauma-Informed Practice': null,
    'Medication Administration': 24,
    'Remote Driving 4WD': 36,
};

/** GET /api/me/compliance */
export function myCompliance(p: Persona, syncedAt: string): Record<string, unknown> {
    if (p.roles.includes('ROLE_BOARD')) {
        return {
            matched: false,
            user_name: p.name,
            user_email: p.email,
            message: 'No Employment Hero employee matches your account email. Board members are not employees.',
            expiring: [],
            essential_training: [],
            workforce_compliance: [],
            mandatory_training: [],
        };
    }
    const key = ME_OVERRIDES[p.key] ? p.key : 'ceo';
    const over = ME_OVERRIDES[key];
    const r = rng(`people:me:${key}`);
    const t = today();
    const d = (n: number) => shiftDay(t, n);

    const trainingItem = (name: string) => {
        const o = over[name];
        const validity = VALIDITY[name] ?? null;
        if (o && (o.status === 'missing' || o.status === 'in_progress')) {
            return { name, status: o.status, course_name: o.status === 'in_progress' ? name : null, completion_date: null, expiry_date: null, progress: o.progress ?? null };
        }
        const expiry = o?.expiresIn !== undefined ? d(o.expiresIn) : validity ? d(r.int(120, validity * 30 - 20)) : null;
        const completion = expiry && validity ? shiftMonths(expiry, -validity) : d(-r.int(60, 700));
        return { name, status: o?.status ?? 'complete', course_name: name, completion_date: completion, expiry_date: expiry, progress: null };
    };

    const wwccDays = r.int(28, 75);
    const workforce = [
        { name: 'Police Clearance', status: 'complete', expiry_date: d(r.int(380, 900)), cert_name: 'National Police Check' },
        { name: 'NDIS Worker Screening Clearance', status: 'complete', expiry_date: d(r.int(900, 1600)), cert_name: 'NDIS Worker Screening Check' },
        { name: 'Working with Children Check', status: 'complete', expiry_date: d(wwccDays), cert_name: WWCC_CERT.NT },
        { name: 'Drivers Licence', status: 'complete', expiry_date: d(r.int(500, 1500)), cert_name: LICENCE_CERT.NT },
    ];

    const expiring = workforce
        .filter((w: any) => w.expiry_date && daysBetween(t, w.expiry_date) <= 90)
        .map((w: any) => {
            const days = daysBetween(t, w.expiry_date);
            return { name: w.name, cert_name: w.cert_name, expiry_date: w.expiry_date, days_remaining: days, status: days < 0 ? 'expired' : 'expiring' };
        })
        .sort((a, b) => a.days_remaining - b.days_remaining);

    return {
        matched: true,
        employee_name: p.name,
        synced_at: syncedAt,
        expiring,
        workforce_compliance: workforce,
        essential_training: ME_ESSENTIAL[key].map(trainingItem),
        mandatory_training: CATEGORY_DEFS[1].items.map(i => trainingItem(i.name)),
        training_status: null,
    };
}
